import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_RISK_MATRIX,
  RECOMMENDED_RISK_MATRIX,
  effectiveMatrixForMode,
  sectorPresetsForMode,
  type EntityMode,
  type RiskMatrixConfig,
  type RiskMatrixResponse,
  type RiskMatrixTemplate,
} from '@cumpli/shared';
import { AlertTriangle } from 'lucide-react';
import { api, RequestError } from '../lib/api';
import { useToast } from '../lib/use-toast';
import { useMe } from '../lib/hooks';
import { Card, EmptyState, PageHeader, Skeleton } from '../components/ui';
import { TabStrip } from '../components/compliance';
import {
  BAND_OVERRIDES_KEY,
  ENABLED_INDICATORS_KEY,
  ENTITY_MODE_COPY,
  SUB_FAMILIES,
  deriveUserDisabled,
  subOf,
  sum,
  type BandOverrides,
  type DisabledIndicators,
  type MatrixController,
  type SubFamilyKey,
  type SubState,
  type Thresholds,
  type Weights,
} from './matriz-riesgo/shared';
import Configuracion from './matriz-riesgo/Configuracion';
import Simulador from './matriz-riesgo/Simulador';

/**
 * Normaliza una plantilla: descarta campos vacíos y devuelve undefined si no queda
 * nada. Se usa para comparar el estado local contra la plantilla guardada (dirty).
 */
function normTpl(t: RiskMatrixTemplate | undefined): RiskMatrixTemplate | undefined {
  if (!t) return undefined;
  const out: RiskMatrixTemplate = {};
  if (t.bandOverrides && Object.keys(t.bandOverrides).length) out.bandOverrides = t.bandOverrides;
  if (t.disabledIndicators && Object.keys(t.disabledIndicators).length)
    out.disabledIndicators = t.disabledIndicators;
  return out.bandOverrides || out.disabledIndicators ? out : undefined;
}

/**
 * `/matriz-riesgo` — a two-tab console page whose visual language matches the guided
 * "Registro de Cliente" journey. Tab 1 (Configuración) is a stepped wizard; Tab 2
 * (Simulador) is a live lab. Both share ONE in-memory matrix via `useMatrixController`.
 */
export default function MatrizRiesgo() {
  const ctrl = useMatrixController();

  const [params, setParams] = useSearchParams();
  const paramTab = params.get('tab') === 'simulador' ? 'simulador' : 'configuracion';
  const [tab, setTab] = useState<'configuracion' | 'simulador'>(paramTab);
  // Keep the active tab in sync with a deep-linked ?tab= (e.g. the Agente opening
  // /matriz-riesgo?tab=simulador while the page is already mounted).
  useEffect(() => setTab(paramTab), [paramTab]);
  function changeTab(next: string) {
    const t = next === 'simulador' ? 'simulador' : 'configuracion';
    setTab(t);
    const p = new URLSearchParams(params);
    if (t === 'configuracion') p.delete('tab');
    else p.set('tab', t);
    setParams(p, { replace: true });
  }

  const subtitle = `Ponderación de factores AML/CFT · ${
    ctrl.version > 0 ? `versión ${ctrl.version}` : 'valores preliminares'
  }`;

  if (ctrl.isLoading) {
    return (
      <div>
        <PageHeader title="Matriz de riesgo" subtitle="Ponderación de factores AML/CFT" />
        <Card>
          <div className="grid gap-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (ctrl.isError) {
    return (
      <div>
        <PageHeader title="Matriz de riesgo" subtitle="Ponderación de factores AML/CFT" />
        <Card>
          <EmptyState
            icon={<AlertTriangle className="h-8 w-8" />}
            title="No se pudo cargar la matriz"
            hint="Hubo un error al obtener la configuración. Recargue la página o contacte soporte si el problema persiste."
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Matriz de riesgo" subtitle={subtitle} />
      <TabStrip
        tabs={[
          { key: 'configuracion', label: 'Configuración' },
          { key: 'simulador', label: 'Simulador' },
        ]}
        active={tab}
        onChange={changeTab}
      />
      {tab === 'configuracion' ? <Configuracion ctrl={ctrl} /> : <Simulador ctrl={ctrl} />}
    </div>
  );
}

/**
 * Single source of truth for the risk-matrix working state, shared by both tabs:
 * the queries, the mutations (save + firm-mode switch), the localStorage template
 * (band overrides + disabled indicators), and every derived value the UI reads.
 */
function useMatrixController(): MatrixController {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const canEdit = !!me && ['compliance_manager', 'tenant_admin'].some((r) => me.roles.includes(r));
  const canSwitchMode = canEdit;

  const { data, isLoading, isError } = useQuery({ queryKey: ['risk-matrix'], queryFn: api.getRiskMatrix });
  const { data: settings } = useQuery({ queryKey: ['tenant-settings'], queryFn: api.getTenantSettings });
  const mode: EntityMode = settings?.entityMode ?? 'financiero';

  const switchModeM = useMutation({
    mutationFn: (next: EntityMode) => api.updateTenantSettings({ entityMode: next }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['tenant-settings'], saved);
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['risk-matrix'] });
      toast({ message: ENTITY_MODE_COPY.updated, kind: 'success' });
    },
    onError: (err) => {
      const msg =
        err instanceof RequestError && err.status === 403
          ? 'No tiene permiso para cambiar el modo de la entidad.'
          : 'No se pudo cambiar el modo de la entidad.';
      toast({ message: msg, kind: 'error' });
    },
  });

  const [weights, setWeights] = useState<Weights>(RECOMMENDED_RISK_MATRIX.weights);
  const [thresholds, setThresholds] = useState<Thresholds>(RECOMMENDED_RISK_MATRIX.thresholds);
  const [subweights, setSubweights] = useState<SubState>(subOf(RECOMMENDED_RISK_MATRIX));
  const [loaded, setLoaded] = useState(false);
  // Configuración tab: saved-view ⇄ wizard. version 0 (first run) always shows the
  // wizard; once saved, the tab lands on the read-only summary until "Editar matriz".
  const [editing, setEditing] = useState(false);

  // Plantilla de indicadores (overrides de banda + indicadores apagados). El estado se
  // siembra de localStorage (borrador local); en la hidratación el servidor gana.
  const [bandOverrides, setBandOverrides] = useState<BandOverrides>(() => {
    try {
      return JSON.parse(localStorage.getItem(BAND_OVERRIDES_KEY) ?? '{}') as BandOverrides;
    } catch {
      return {};
    }
  });
  const [disabledIndicators, setDisabledIndicators] = useState<DisabledIndicators>(() => {
    try {
      return JSON.parse(localStorage.getItem(ENABLED_INDICATORS_KEY) ?? '{}') as DisabledIndicators;
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (data && !loaded) {
      setWeights(data.weights);
      setThresholds(data.thresholds);
      setSubweights(subOf(data));
      // Server-first: si la respuesta trae plantilla no vacía, gana sobre el borrador
      // local. Si no trae, se conserva lo sembrado de localStorage — migración de una
      // sola vez que se persistirá al servidor en el próximo Guardar.
      if (data.template?.bandOverrides && Object.keys(data.template.bandOverrides).length)
        setBandOverrides(data.template.bandOverrides);
      if (data.template?.disabledIndicators && Object.keys(data.template.disabledIndicators).length)
        setDisabledIndicators(data.template.disabledIndicators);
      setLoaded(true);
    }
  }, [data, loaded]);

  const total = weights.cliente + weights.jurisdiccion + weights.canal + weights.producto;
  const sumOk = total === 100;
  const thresholdsOk = thresholds.alto > thresholds.medio && thresholds.medio >= 1 && thresholds.alto <= 100;
  const subSumsOk = SUB_FAMILIES.every((f) => sum(subweights[f]) === 100);
  const canSave = sumOk && thresholdsOk && subSumsOk;

  const matrix: RiskMatrixConfig = useMemo(
    () => ({ weights, thresholds, subweights }),
    [weights, thresholds, subweights]
  );

  // Plantilla a persistir con la matriz: solo campos no vacíos; undefined si no hay nada.
  const template = useMemo<RiskMatrixTemplate | undefined>(() => {
    const t: RiskMatrixTemplate = {};
    if (Object.keys(bandOverrides).length) t.bandOverrides = bandOverrides;
    if (Object.keys(disabledIndicators).length)
      t.disabledIndicators = disabledIndicators as Record<string, Record<string, string[]>>;
    return t.bandOverrides || t.disabledIndicators ? t : undefined;
  }, [bandOverrides, disabledIndicators]);

  const dirty = useMemo(
    () =>
      !!data &&
      (JSON.stringify(weights) !== JSON.stringify(data.weights) ||
        JSON.stringify(thresholds) !== JSON.stringify(data.thresholds) ||
        JSON.stringify(subweights) !== JSON.stringify(subOf(data)) ||
        // La plantilla de indicadores también cuenta como cambio sin guardar.
        // ponytail: comparación por JSON, sensible al orden de claves — a lo sumo
        // deja Guardar habilitado de más (re-guardar idéntico es inocuo).
        JSON.stringify(template ?? null) !== JSON.stringify(normTpl(data.template) ?? null)),
    [data, weights, thresholds, subweights, template]
  );

  const activePreset: 'recomendado' | 'preliminar' | null = useMemo(() => {
    const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    if (eq(matrix, RECOMMENDED_RISK_MATRIX)) return 'recomendado';
    if (eq({ weights, thresholds }, { weights: DEFAULT_RISK_MATRIX.weights, thresholds: DEFAULT_RISK_MATRIX.thresholds }))
      return 'preliminar';
    return null;
  }, [matrix, weights, thresholds]);

  const modePresets = useMemo(() => sectorPresetsForMode(mode), [mode]);
  const activeSector = useMemo(
    () => modePresets.find((p) => JSON.stringify(p.weights) === JSON.stringify(weights)),
    [modePresets, weights]
  );

  // Cache local (borrador offline): la fuente de verdad al cargar es el servidor.
  useEffect(() => {
    try {
      localStorage.setItem(BAND_OVERRIDES_KEY, JSON.stringify(bandOverrides));
    } catch {
      /* almacenamiento no disponible — la edición sigue viva en memoria */
    }
  }, [bandOverrides]);
  useEffect(() => {
    try {
      localStorage.setItem(ENABLED_INDICATORS_KEY, JSON.stringify(disabledIndicators));
    } catch {
      /* almacenamiento no disponible — la edición sigue viva en memoria */
    }
  }, [disabledIndicators]);

  const disabledForMode = disabledIndicators[mode] ?? {};
  const userDisabled = useMemo(
    () => deriveUserDisabled(mode, disabledIndicators[mode] ?? {}),
    [mode, disabledIndicators]
  );
  const setDisabledForMode = (next: Record<string, string[]>) =>
    setDisabledIndicators((d) => {
      const copy = { ...d };
      if (Object.keys(next).length === 0) delete copy[mode];
      else copy[mode] = next;
      return copy;
    });

  const effectiveMatrix = useMemo(
    () => effectiveMatrixForMode(mode, matrix, userDisabled),
    [mode, matrix, userDisabled]
  );

  const saveM = useMutation({
    mutationFn: () => api.saveRiskMatrix(template ? { ...matrix, template } : matrix),
    onSuccess: (saved: RiskMatrixResponse) => {
      queryClient.setQueryData(['risk-matrix'], saved);
      setWeights(saved.weights);
      setThresholds(saved.thresholds);
      setSubweights(subOf(saved));
      setBandOverrides(saved.template?.bandOverrides ?? {});
      setDisabledIndicators(saved.template?.disabledIndicators ?? {});
      // Finishing the wizard lands you on the finished matrix (saved view).
      setEditing(false);
      toast({ message: `Matriz guardada (versión ${saved.version})`, kind: 'success' });
    },
    onError: (err) => {
      const msg =
        err instanceof RequestError && err.status === 403
          ? 'No tiene permiso para guardar la matriz.'
          : 'No se pudo guardar la matriz.';
      toast({ message: msg, kind: 'error' });
    },
  });

  function setWeight(key: keyof Weights, value: number) {
    setWeights((w) => ({ ...w, [key]: Math.max(0, Math.min(100, value || 0)) }));
  }
  function setSub(family: SubFamilyKey, key: string, value: number) {
    setSubweights((s) => ({
      ...s,
      [family]: { ...s[family], [key]: Math.max(0, Math.min(100, value || 0)) },
    }));
  }
  function applyPreset(preset: RiskMatrixConfig) {
    setWeights(preset.weights);
    setThresholds(preset.thresholds);
    setSubweights(subOf(preset));
  }
  function resetToSaved() {
    if (!data) return;
    setWeights(data.weights);
    setThresholds(data.thresholds);
    setSubweights(subOf(data));
  }
  // Cancelar edición: discard unsaved wizard changes (weights + indicator template)
  // and return to the saved view. Mirrors the save-success rehydration, from `data`.
  function cancelEdit() {
    resetToSaved();
    setBandOverrides(data?.template?.bandOverrides ?? {});
    setDisabledIndicators(data?.template?.disabledIndicators ?? {});
    setEditing(false);
  }

  return {
    isLoading,
    isError,
    savedMatrix: data,
    version: data?.version ?? 0,
    canEdit,
    canSwitchMode,
    mode,
    switchMode: (next) => {
      if (next !== mode) switchModeM.mutate(next);
    },
    switching: switchModeM.isPending,
    weights,
    thresholds,
    subweights,
    setWeight,
    setSub,
    setThresholds,
    applyPreset,
    bandOverrides,
    setBandOverrides,
    disabledForMode,
    setDisabledForMode,
    userDisabled,
    matrix,
    effectiveMatrix,
    total,
    sumOk,
    thresholdsOk,
    subSumsOk,
    canSave,
    dirty,
    save: () => saveM.mutate(),
    saving: saveM.isPending,
    resetToSaved,
    editing,
    startEdit: () => setEditing(true),
    cancelEdit,
    modePresets,
    activePreset,
    activeSector,
  };
}
